class AdawatLuckyDicePopup extends HTMLElement {
  static storageKey = 'adawat-lucky-dice-played';
  static bagsStorageKey = 'adawat-lucky-bags-played';
  static ttlMs = 30 * 24 * 60 * 60 * 1000;

  /* Die A + Die B → percent. Weights = odds out of 100. Stacks with sitewide sale (e.g. 40% + dice). */
  static prizes = [
    { pct: 11, a: 1, b: 1, weight: 36 }, /* common */
    { pct: 15, a: 1, b: 5, weight: 28 }, /* common + */
    { pct: 22, a: 2, b: 2, weight: 20 }, /* common ++ */
    { pct: 25, a: 2, b: 5, weight: 12 }, /* common +++ */
    { pct: 31, a: 3, b: 1, weight: 3 }, /* rare — 3% */
    { pct: 35, a: 3, b: 5, weight: 1 }, /* rarest — 1% */
  ];

  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    try {
      const path = String(window.location.pathname || '').toLowerCase();
      if (path.includes('/pages/adawat-ops') || path.includes('/pages/adawat-create-order')) {
        this.remove();
        return;
      }
    } catch (_) {
      /* ignore */
    }

    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.rtl = document.documentElement.dir === 'rtl';
    this.delayAfterNl = Number(this.dataset.delayAfterNl || 700);
    this.playBtn = this.querySelector('[data-ld-play]');
    this.playWrap = this.querySelector('[data-ld-play-wrap]');
    this.result = this.querySelector('[data-ld-result]');
    this.pctEl = this.querySelector('[data-ld-pct]');
    this.codeEl = this.querySelector('[data-ld-code]');
    this.noteEl = this.querySelector('[data-ld-note]');
    this.shopBtn = this.querySelector('[data-ld-shop]');
    this.dialog = this.querySelector('.adawat-ld__dialog');
    this.dieA = this.querySelector('[data-ld-die="a"]');
    this.dieB = this.querySelector('[data-ld-die="b"]');
    this.played = false;
    this.rolling = false;
    this.won = null;
    this._prevFocus = null;

    this.querySelectorAll('[data-ld-close], [data-ld-close-backdrop]').forEach((btn) => {
      btn.addEventListener('click', () => this.dismiss(true));
    });
    this.playBtn?.addEventListener('click', () => this.play());
    this.shopBtn?.addEventListener('click', () => this.shopNow());

    /* Resting pose: front 1, top 2, left 4 (matches reference). */
    this.setDieFace(this.dieA, 1, { animate: false });
    this.setDieFace(this.dieB, 1, { animate: false });

    this._onKey = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        event.preventDefault();
        this.dismiss(true);
      }
    };
    document.addEventListener('keydown', this._onKey);

    this._onNewsletterClosed = () => this.scheduleOpen('newsletter');
    window.addEventListener('adawat:newsletter-closed', this._onNewsletterClosed);

    if (this.isDesignMode()) {
      this.open();
      return;
    }

    if (this.isPreviewMode()) {
      this.resetForPreview();
      window.setTimeout(() => this.open(), 400);
      return;
    }

    if (this.hasPlayed()) {
      this.hide();
      return;
    }

    window.setTimeout(() => this.maybeOpenWithoutNewsletter(), 1800);
  }

  prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
      return false;
    }
  }

  isPreviewMode() {
    try {
      return new URLSearchParams(window.location.search).has('ld_preview');
    } catch (_) {
      return false;
    }
  }

  resetForPreview() {
    try {
      window.localStorage.removeItem(AdawatLuckyDicePopup.storageKey);
    } catch (_) {}
    this.played = false;
    this.rolling = false;
    this.won = null;
    this.classList.remove('is-rolling', 'is-landed', 'is-won');
    if (this.result) this.result.hidden = true;
    if (this.playWrap) this.playWrap.hidden = false;
    if (this.playBtn) this.playBtn.disabled = false;
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('adawat:newsletter-closed', this._onNewsletterClosed);
    window.clearTimeout(this._openTimer);
    window.clearInterval(this._bagsWait);
    this.unlockScroll();
  }

  isDesignMode() {
    return Boolean(window.Shopify?.designMode) || document.documentElement.classList.contains('shopify-design-mode');
  }

  storagePlayed(key) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return false;
      const ts = Number(raw);
      if (!Number.isFinite(ts)) return true;
      return Date.now() - ts < AdawatLuckyDicePopup.ttlMs;
    } catch (_) {
      return false;
    }
  }

  hasPlayed() {
    return this.storagePlayed(AdawatLuckyDicePopup.storageKey);
  }

  bagsStillPending() {
    const bags = document.getElementById('AdawatLuckyBagsPopup');
    if (!bags) return false;
    if (this.storagePlayed(AdawatLuckyDicePopup.bagsStorageKey)) return false;
    return bags.classList.contains('is-open') || !bags.hasAttribute('hidden');
  }

  bagsMayStillOpen() {
    const bags = document.getElementById('AdawatLuckyBagsPopup');
    if (!bags) return false;
    return !this.storagePlayed(AdawatLuckyDicePopup.bagsStorageKey);
  }

  persistPlayed() {
    try {
      window.localStorage.setItem(AdawatLuckyDicePopup.storageKey, String(Date.now()));
    } catch (_) {
      /* ignore */
    }
  }

  newsletterStillVisible() {
    const nl = document.getElementById('AdawatNewsletterPopup');
    return Boolean(nl && nl.classList.contains('is-open'));
  }

  maybeOpenWithoutNewsletter() {
    if (this.hasPlayed() || this.classList.contains('is-open') || this._scheduled) return;
    if (this.newsletterStillVisible()) return;
    const nl = document.getElementById('AdawatNewsletterPopup');
    if (!nl || nl.hasAttribute('hidden') || !nl.classList.contains('is-open')) {
      this.scheduleOpen('fallback');
    }
  }

  scheduleOpen(reason) {
    if (this.hasPlayed() || this._scheduled) return;
    this._scheduled = true;
    window.clearTimeout(this._openTimer);
    const wait = reason === 'newsletter' ? this.delayAfterNl : Math.max(400, this.delayAfterNl);
    this._openTimer = window.setTimeout(() => this.tryOpenAfterBags(), Number.isFinite(wait) ? wait : 700);
  }

  tryOpenAfterBags() {
    if (this.hasPlayed()) return;
    if (this.newsletterStillVisible()) {
      this._scheduled = false;
      return;
    }
    if (!this.bagsMayStillOpen() && !this.bagsStillPending()) {
      this.open();
      return;
    }
    let ticks = 0;
    window.clearInterval(this._bagsWait);
    this._bagsWait = window.setInterval(() => {
      ticks += 1;
      if (this.hasPlayed()) {
        window.clearInterval(this._bagsWait);
        return;
      }
      if (this.newsletterStillVisible()) return;
      if (!this.bagsStillPending() && (this.storagePlayed(AdawatLuckyDicePopup.bagsStorageKey) || ticks > 40)) {
        window.clearInterval(this._bagsWait);
        this.open();
      }
      if (ticks > 120) {
        window.clearInterval(this._bagsWait);
        this.open();
      }
    }, 400);
  }

  open() {
    this._prevFocus = document.activeElement;
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    this.lockScroll();
    window.requestAnimationFrame(() => {
      (this.playBtn || this.querySelector('[data-ld-close]') || this.dialog)?.focus?.();
    });
  }

  dismiss(persist) {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    if (persist && (this.played || this.won)) this.persistPlayed();
    window.setTimeout(() => {
      if (!this.classList.contains('is-open')) this.hide();
      if (this._prevFocus && typeof this._prevFocus.focus === 'function') {
        try {
          this._prevFocus.focus();
        } catch (_) {}
      }
    }, 280);
  }

  hide() {
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
  }

  pickPrize() {
    const list = AdawatLuckyDicePopup.prizes;
    const total = list.reduce((sum, row) => sum + row.weight, 0);
    let roll = Math.random() * total;
    for (const row of list) {
      roll -= row.weight;
      if (roll <= 0) return row;
    }
    return list[list.length - 1];
  }

  codeFor(pct) {
    const key = `code${pct}`;
    const fromDom = String(this.dataset[key] || '').trim();
    /* Never fall back to guessable DICE11 / DICE35. */
    if (fromDom && !/^DICE\d+$/i.test(fromDom)) return fromDom;
    const fallback = {
      11: 'ADLD-K7M2QX',
      15: 'ADLD-R9N4WP',
      22: 'ADLD-H3V8TZ',
      25: 'ADLD-B6C1YJ',
      31: 'ADLD-F5Q9LM',
      35: 'ADLD-W2P7NK',
    };
    return fallback[pct] || fromDom || '';
  }

  setDieFace(el, value, { animate = true, spins = 0 } = {}) {
    if (!el) return;
    const face = Math.max(1, Math.min(6, Number(value) || 1));
    el.dataset.face = String(face);
    el.setAttribute('aria-label', String(face));
    const cube = el.querySelector('[data-ld-cube]');
    if (!cube) return;

    /* Map face → rotation that brings that face to the front. */
    const map = {
      1: { x: 0, y: 0 },
      2: { x: -90, y: 0 },
      3: { x: 0, y: -90 },
      4: { x: 0, y: 90 },
      5: { x: 90, y: 0 },
      6: { x: 0, y: 180 },
    };
    const target = map[face] || map[1];
    const spinX = 360 * Math.max(0, spins);
    const spinY = 360 * Math.max(0, Math.round(spins * 1.35));
    const transform = `rotateX(${spinX + target.x}deg) rotateY(${spinY + target.y}deg)`;

    if (!animate || this.prefersReducedMotion()) {
      const prev = cube.style.transition;
      cube.style.transition = 'none';
      cube.style.transform = transform;
      void cube.offsetWidth;
      cube.style.transition = prev || '';
      return;
    }
    cube.style.transform = transform;
  }

  async play() {
    if (this.played || this.rolling) return;
    this.rolling = true;
    this.played = true;
    this.classList.remove('is-landed');
    this.classList.add('is-rolling');
    if (this.playBtn) this.playBtn.disabled = true;

    const prize = this.pickPrize();
    this.won = { ...prize, code: this.codeFor(prize.pct) };

    const reduced = this.prefersReducedMotion();
    if (!reduced) this.playRollSound();

    await new Promise((resolve) => window.setTimeout(resolve, reduced ? 80 : 1600));
    this.classList.remove('is-rolling');
    this.stopRollSound();

    if (!reduced) {
      [this.dieA, this.dieB].forEach((die) => {
        const cube = die?.querySelector?.('[data-ld-cube]');
        if (!cube) return;
        cube.style.transition = 'none';
        cube.style.transform = `rotateX(${720 + Math.floor(Math.random() * 180)}deg) rotateY(${-540 - Math.floor(Math.random() * 180)}deg)`;
        void cube.offsetWidth;
        cube.style.transition = '';
      });
    }

    this.setDieFace(this.dieA, prize.a, { animate: !reduced, spins: reduced ? 0 : 2 });
    this.setDieFace(this.dieB, prize.b, { animate: !reduced, spins: reduced ? 0 : 2 });
    this.classList.add('is-landed');
    this.rolling = false;

    await new Promise((resolve) => window.setTimeout(resolve, reduced ? 120 : 900));

    if (this.pctEl) this.pctEl.textContent = `${prize.pct}%`;
    /* Never show the discount code in the win popup (prevents sharing / guessing). */
    const codeRow = this.querySelector('[data-ld-code-row]');
    if (codeRow) codeRow.hidden = true;
    if (this.codeEl) {
      this.codeEl.textContent = '';
      this.codeEl.hidden = true;
    }
    if (this.noteEl) {
      this.noteEl.textContent = this.rtl
        ? 'الخصم مفعّل في السلة تحت الإجمالي.'
        : 'Discount is on in your cart under the total.';
    }
    if (this.result) this.result.hidden = false;
    if (this.playWrap) this.playWrap.hidden = true;
    this.classList.add('is-won');
    if (!reduced) this.playWinSound();

    this.persistPlayed();
    await this.applyPrize(this.won);
  }

  audioContext() {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!this._audioCtx) this._audioCtx = new Ctx();
    if (this._audioCtx.state === 'suspended') {
      this._audioCtx.resume().catch(() => {});
    }
    return this._audioCtx;
  }

  stopRollSound() {
    const audio = this._rollAudio;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch (_) {
      /* ignore */
    }
  }

  playRollSound() {
    const src = this.dataset.rollSrc;
    if (src) {
      try {
        if (!this._rollAudio || this._rollAudioSrc !== src) {
          this._rollAudio = new Audio(src);
          this._rollAudio.preload = 'auto';
          this._rollAudioSrc = src;
        }
        const audio = this._rollAudio;
        audio.volume = 0.85;
        audio.currentTime = 0;
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this.playRollSoundFallback());
        }
        return;
      } catch (_) {
        /* fall through to synth */
      }
    }
    this.playRollSoundFallback();
  }

  playRollSoundFallback() {
    const ctx = this.audioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    const duration = 1.55;
    const hits = [0.02, 0.09, 0.16, 0.24, 0.33, 0.43, 0.54, 0.67, 0.82, 0.98, 1.15, 1.32, 1.45];
    hits.forEach((t, index) => {
      const amp = (0.55 - index * 0.025) * (0.7 + Math.random() * 0.3);
      const noiseDur = 0.025;
      const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * noiseDur), ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.008));
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 900 + Math.random() * 1400;
      filter.Q.value = 1.2;
      const gain = ctx.createGain();
      const start = now + t;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.05, amp), start + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.05);
      source.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      source.start(start);
      source.stop(start + noiseDur + 0.02);
    });
    // soft scrape underlay
    const scrape = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * duration), ctx.sampleRate);
    const scrapeData = scrape.getChannelData(0);
    for (let i = 0; i < scrapeData.length; i += 1) {
      const t = i / ctx.sampleRate;
      scrapeData[i] = (Math.random() * 2 - 1) * (1 - t / duration) * 0.08;
    }
    const scrapeSrc = ctx.createBufferSource();
    scrapeSrc.buffer = scrape;
    const scrapeGain = ctx.createGain();
    scrapeGain.gain.value = 0.45;
    scrapeSrc.connect(scrapeGain);
    scrapeGain.connect(ctx.destination);
    scrapeSrc.start(now);
    scrapeSrc.stop(now + duration);
  }

  playWinSound() {
    const ctx = this.audioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = index === notes.length - 1 ? 'triangle' : 'sine';
      osc.frequency.value = freq;
      const start = now + index * 0.12;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.28, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.55);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.6);
    });
  }

  /**
   * Integration point for cart discount. Uses existing AdawatCart when present.
   * Override via window.AdawatLuckyDiceApplyPrize = async (prize) => {}
   */
  async applyPrize(prize) {
    if (typeof window.AdawatLuckyDiceApplyPrize === 'function') {
      await window.AdawatLuckyDiceApplyPrize(prize, this);
      return;
    }

    const attrs = {
      lucky_dice_pct: String(prize.pct),
      lucky_dice_code: prize.code,
    };
    try {
      if (window.AdawatCart?.applyDiscount) {
        await window.AdawatCart.applyDiscount(prize.code, {
          openDrawer: false,
          attributes: attrs,
        });
      } else {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: prize.code, attributes: attrs }),
        });
      }
    } catch (_) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: prize.code, attributes: attrs }),
        });
      } catch (_) {
        /* Continue shopping re-applies via /discount/ URL */
      }
    }
  }

  async shopNow() {
    const home = window.Shopify?.routes?.root || '/';
    const code = this.won?.code;
    if (code && window.AdawatCart?.applyDiscount) {
      try {
        await window.AdawatCart.applyDiscount(code, { openDrawer: false });
      } catch (_) {
        /* still send them home */
      }
    }
    this.dismiss(true);
    if (code) {
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(home)}`;
      return;
    }
    window.location.href = home;
  }

  lockScroll() {
    if (this._locked) return;
    this._locked = true;
    this._scrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${this._scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!this._locked) return;
    this._locked = false;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    document.documentElement.style.overflow = '';
    window.scrollTo(0, this._scrollY || 0);
  }
}

if (!customElements.get('adawat-lucky-dice-popup')) {
  customElements.define('adawat-lucky-dice-popup', AdawatLuckyDicePopup);
}
